import { showNotification } from "@mantine/notifications"
import { Button } from "@tfg/ui"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  ExportConfigurationDto,
  getExportConfigurationsQueryKey,
  useGenerateExport,
  useLoadExportConfigurations,
} from "~/gen/api"
import { useAuth } from "~/store/auth-context"

type Props = {
  selectedExportConfiguration: ExportConfigurationDto | undefined
}

export const GenerateExportActions: React.FC<Props> = ({
  selectedExportConfiguration,
}: Props) => {
  const { t } = useTranslation("dataTransfer")

  const { isAdmin } = useAuth()

  const { mutate: generateExport } = useGenerateExport()

  const { mutate: loadExportConfigurations } = useLoadExportConfigurations()

  const onClickAction = useCallback(() => {
    if (!selectedExportConfiguration) {
      return
    }

    return generateExport(
      {
        data: {
          exportConfigurationId: selectedExportConfiguration.id,
        },
      },
      {
        onSuccess() {
          showNotification({
            color: "green",
            message: t("export.notifications.generate.success"),
          })

          queryClient.invalidateQueries({
            queryKey: [{ url: "/v1/export/export-requests" }],
          })
        },
      }
    )
  }, [selectedExportConfiguration, generateExport, t])

  return (
    <>
      <Button onClick={onClickAction} disabled={!selectedExportConfiguration}>
        {t("export.generate")}
      </Button>
      {isAdmin ? (
        <Button
          onClick={() => {
            loadExportConfigurations(null as never, {
              onSuccess() {
                showNotification({
                  color: "green",
                  message: t("export.notifications.load.success"),
                })

                queryClient.invalidateQueries({
                  queryKey: getExportConfigurationsQueryKey(),
                })
              },
            })
          }}
          variant="admin"
        >
          {t("export.load")}
        </Button>
      ) : null}
    </>
  )
}
